import { Trans, useLingui } from '@lingui/react/macro';

import { useGetDateFNSLocale } from '@/locales/helpers';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { parseAndFormatDate } from '@/utils/parseDate';

import { type VisitIdToImagesMap } from './hooks/usePatientPrintoutData';
import { PatientPrintoutLayout } from './PatientPrintoutLayout';
import { VisitSection } from './VisitSection';

interface Props {
  isLoading: boolean;
  scale?: number;
  visitSummaries: TVisitSummary[];
  visitIdToImagesMap: VisitIdToImagesMap;
}

export const PatientPrintout = ({
  isLoading,
  scale = 1,
  visitSummaries,
  visitIdToImagesMap,
}: Props) => {
  const { t } = useLingui();
  const visitSummaryData = visitSummaries || [];
  const datefnsLocale = useGetDateFNSLocale();

  const getVisitTitle = (visit: VisitIdToImagesMap[number]) => {
    if (visitIdToImagesMap.length > 1) {
      const date = visit.images[0]?.imageAcquiredAt
        ? parseAndFormatDate(
            new Date(visit.images[0].imageAcquiredAt).toISOString(),
            'MMM. d, yyyy',
            datefnsLocale,
          )
        : t`Historical`;
      return `${t`Visit `} - ${date}`;
    }
    return <Trans>AI Analyzed Image</Trans>;
  };

  if (!visitSummaryData.length) {
    return null;
  }

  return (
    <PatientPrintoutLayout scale={scale}>
      <div className="vai-flex vai-flex-col vai-gap-4">
        {visitIdToImagesMap.map((visit, index) => {
          if (!visit.images.length) {
            return null;
          }

          return (
            <VisitSection
              key={visit.estimatedVisitId}
              images={visit.images}
              visitSummary={visitSummaryData.find(
                (summary) =>
                  summary.estimatedVisitId === visit.estimatedVisitId,
              )}
              isLoading={isLoading}
              position={index % 2 === 0 ? 'left' : 'right'}
              title={getVisitTitle(visit)}
            />
          );
        })}
      </div>
    </PatientPrintoutLayout>
  );
};
